import React, { useState, useMemo, useEffect } from 'react';
import { Button, Modal, Form, Select, message, Spin } from 'antd';
import {
  LeftOutlined,
  // QuestionCircleOutlined,
  DollarOutlined,
  HomeOutlined,
  PlusOutlined,
  BankOutlined,
} from '@ant-design/icons';
import {
  GoalType,
  calculateFinalDistribution,
  QuestionAssessmentResults,
  Question,
  adjustAllocations,
  AccountType,
  accountTypeToGoalType,
  RetirementType,
} from '@/util/planning-util';
import './step1.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { Recommendation, TimeHorizonCollection } from '@/util/account-openning-util';
import { selectApexAccounts } from '@/store/features/apex/apex-selectors';
import { fetchApexAccounts } from '@/store/features/apex/apex-thunks';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectRiaProfile } from '@/store/features/userinfo/userinfoSlice';

const { Option } = Select;

interface GoalOption {
  goal: GoalType;
  icon: React.ReactNode;
  disabled?: boolean;
}

interface RetirementOption {
  type: RetirementType;
  title: string;
  description: string;
  disabled?: boolean;
}

type ExistingRetirementTypes = Set<RetirementType>;

type RiskRetakeLocationState = {
  retakeRiskOnly?: boolean;
  returnPath?: string;
  accountId?: string | null;
};

const DEFAULT_RISK_RETAKE_RETURN_PATH = '/home/wealth-profile';

const safeRiskRetakeReturnPath = (returnPath: string | null | undefined): string =>
  returnPath && returnPath.startsWith('/') && !returnPath.startsWith('//')
    ? returnPath
    : DEFAULT_RISK_RETAKE_RETURN_PATH;

interface RiskAnswer {
  id: string;
  answer: string;
  'Points Towards Growth': number;
  'Points Towards capital preservation': number;
  'Points Towards Yield': number;
}

interface RiskQuestion {
  context: string;
  question: string;
  answers: RiskAnswer[];
}

// goal question mapping
const GOAL_QUESTIONS: Record<GoalType, string> = {
  'Just Invest': 'How many years do you plan to invest?',
  Retirement: 'How many years until retirement?',
  'Major Purchase': 'How many years until this purchase?',
  'Supplement Income': 'How many years until income withdrawal?',
  'High Yield Cash': 'How many years until cash withdrawal?',
};

// risk assessment questions
const RISK_QUESTIONS: RiskQuestion[] = [
  {
    context: 'Financial Risk Tolerance',
    question: 'How do you feel about financial risk in your portfolio?',
    answers: [
      {
        id: 'a',
        answer: '🎲 I’m all in — high risk, high reward!',
        'Points Towards Growth': 16.75,
        'Points Towards capital preservation': 0,
        'Points Towards Yield': 0,
      },
      {
        id: 'b',
        answer: '🚀 I’m comfortable with higher risk for potentially greater rewards.',
        'Points Towards Growth': 13.4,
        'Points Towards capital preservation': 2.233333333,
        'Points Towards Yield': 1.116666667,
      },
      {
        id: 'c',
        answer: '⚖️ I’m okay with some risk if it means higher returns.',
        'Points Towards Growth': 10.05,
        'Points Towards capital preservation': 4.466666667,
        'Points Towards Yield': 2.233333333,
      },
      {
        id: 'd',
        answer: '🛡️ I prefer very low risk — modest growth is fine.',
        'Points Towards Growth': 6.7,
        'Points Towards capital preservation': 6.7,
        'Points Towards Yield': 3.35,
      },
      {
        id: 'e',
        answer: '🚫 I avoid risk at all costs — I want stability above all.',
        'Points Towards Growth': 0,
        'Points Towards capital preservation': 11.16666667,
        'Points Towards Yield': 5.583333333,
      },
    ],
  },
  {
    context: 'Comfort With Losses',
    question:
      'If you had $100,000 and it dropped in value, what’s the most loss you could tolerate before feeling the need to make changes?',
    answers: [
      {
        id: 'a',
        answer: '💥 A $50,000 drop (-50%).',
        'Points Towards Growth': 16.75,
        'Points Towards capital preservation': 0,
        'Points Towards Yield': 0,
      },
      {
        id: 'b',
        answer: '📉 A $30,000 drop (-30%).',
        'Points Towards Growth': 13.4,
        'Points Towards capital preservation': 2.233333333,
        'Points Towards Yield': 1.116666667,
      },
      {
        id: 'c',
        answer: '📊 A $15,000 drop (-15%).',
        'Points Towards Growth': 10.05,
        'Points Towards capital preservation': 4.466666667,
        'Points Towards Yield': 2.233333333,
      },
      {
        id: 'd',
        answer: '🪙 A $5,000 drop (-5%).',
        'Points Towards Growth': 6.7,
        'Points Towards capital preservation': 6.7,
        'Points Towards Yield': 3.35,
      },
      {
        id: 'e',
        answer: '💤 Any loss would make me uncomfortable.',
        'Points Towards Growth': 0,
        'Points Towards capital preservation': 11.16666667,
        'Points Towards Yield': 5.583333333,
      },
    ],
  },
  {
    context: 'Behavioral Response to Market Downturns',
    question: 'What did you do with your investments during the 2020 Covid market sell-off?',
    answers: [
      {
        id: 'a',
        answer: '💪 I invested heavily to take advantage of the crash.',
        'Points Towards Growth': 16.75,
        'Points Towards capital preservation': 0,
        'Points Towards Yield': 0,
      },
      {
        id: 'b',
        answer: '📈 I bought a little more while prices were down.',
        'Points Towards Growth': 13.4,
        'Points Towards capital preservation': 2.233333333,
        'Points Towards Yield': 1.116666667,
      },
      {
        id: 'c',
        answer: '😐 I stayed the course and did nothing.',
        'Points Towards Growth': 10.05,
        'Points Towards capital preservation': 4.466666667,
        'Points Towards Yield': 2.233333333,
      },
      {
        id: 'd',
        answer: '😬 I sold some investments but kept most.',
        'Points Towards Growth': 6.7,
        'Points Towards capital preservation': 6.7,
        'Points Towards Yield': 3.35,
      },
      {
        id: 'e',
        answer: '😱 I sold everything to avoid further losses.',
        'Points Towards Growth': 0,
        'Points Towards capital preservation': 11.16666667,
        'Points Towards Yield': 5.583333333,
      },
    ],
  },
  {
    context: 'Fun, General Risk Scenario',
    question: 'You’re given $1,000 to invest. How would you allocate it?',
    answers: [
      {
        id: 'a',
        answer:
          '🎲 Put it all in crypto or a single promising stock for max upside (and downside).',
        'Points Towards Growth': 16.75,
        'Points Towards capital preservation': 0,
        'Points Towards Yield': 0,
      },
      {
        id: 'b',
        answer: '📊 Invest it all in a diversified stock ETF for long-term growth.',
        'Points Towards Growth': 13.4,
        'Points Towards capital preservation': 2.233333333,
        'Points Towards Yield': 1.116666667,
      },
      {
        id: 'c',
        answer: '⚖️ Split it between stocks and bonds: moderate risk, moderate return.',
        'Points Towards Growth': 10.05,
        'Points Towards capital preservation': 4.466666667,
        'Points Towards Yield': 2.233333333,
      },
      {
        id: 'd',
        answer: '🧾 Buy government bonds for safe, predictable returns.',
        'Points Towards Growth': 6.7,
        'Points Towards capital preservation': 6.7,
        'Points Towards Yield': 3.35,
      },
      {
        id: 'e',
        answer: '🏦 Put it all in a high-yield savings account for a guaranteed small gain',
        'Points Towards Growth': 0,
        'Points Towards capital preservation': 11.16666667,
        'Points Towards Yield': 5.583333333,
      },
    ],
  },
];

const Step1: React.FC = () => {
  const dispatch = useAppDispatch();
  const accounts = useAppSelector(selectApexAccounts);
  const riaProfile = useAppSelector(selectRiaProfile);
  // Firm-data is not used to disable goals — fo-apex account_investment_type
  // (JUST_INVEST, RETIREMENT_ROTH, …) is the source of truth for this wizard,
  // independent of whether firm-data is enabled for the firm.
  const shouldIncludeApex =
    Boolean(riaProfile.profile?.apex_correspondent_id) &&
    Boolean(riaProfile.features?.apex_enabled);
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as RiskRetakeLocationState | null;
  const searchParams = new URLSearchParams(location.search);
  const retakeRiskOnly =
    Boolean(routeState?.retakeRiskOnly) || searchParams.get('retakeRiskOnly') === 'true';
  const returnPath = safeRiskRetakeReturnPath(
    routeState?.returnPath ?? searchParams.get('returnPath'),
  );
  const retakeAccountId = routeState?.accountId ?? searchParams.get('accountId') ?? null;

  // Use useMemo to stabilize these computed values
  const existingGoals: Set<GoalType> = useMemo(
    () => new Set(accounts.map((account) => accountTypeToGoalType(account.type as AccountType))),
    [accounts],
  );

  const existingRetirementTypes: ExistingRetirementTypes = useMemo(
    () =>
      new Set(
        accounts
          .filter((a) => a.type.startsWith('RETIREMENT_'))
          .map((a) => a.type.split('_')[1] as RetirementType),
      ),
    [accounts],
  );

  // Hold goal selection until Apex accounts are known so we don't flash
  // Just Invest / retirement subtypes as available, then disable them.
  const [apexGoalsReady, setApexGoalsReady] = useState(
    () => !shouldIncludeApex || accounts.length > 0,
  );

  useEffect(() => {
    if (!shouldIncludeApex) {
      setApexGoalsReady(true);
      return;
    }

    let cancelled = false;

    const markReady = () => {
      if (!cancelled) {
        setApexGoalsReady(true);
      }
    };

    // A failed fetch means we can't tell which Apex goals the investor
    // already has, so retry once before falling back to unlocking the
    // goal list — never leave the page stuck on a spinner, but give the
    // duplicate-goal check a second chance to actually get real data.
    const attemptFetch = (isRetry: boolean) => {
      const request = dispatch(fetchApexAccounts()) as {
        unwrap?: () => Promise<unknown>;
        abort?: () => void;
      };

      if (typeof request?.unwrap === 'function') {
        void request.unwrap().then(markReady, () => {
          if (cancelled) return;
          if (isRetry) {
            console.error('Failed to load Apex accounts for goal locking after retry');
            markReady();
          } else {
            attemptFetch(true);
          }
        });
      } else {
        markReady();
      }

      return request;
    };

    const request = attemptFetch(false);

    return () => {
      cancelled = true;
      if (typeof request?.abort === 'function') {
        request.abort();
      }
    };
  }, [dispatch, shouldIncludeApex]);

  const isFromAddAccount = true;

  const [form] = Form.useForm();
  // const [existingGoalList, setExistingoalList] = useState<Set<GoalType>>(new Set());
  // const [existingRetirementTypeList, setExistingRetirementTypeList] = useState<Set<RetirementType>>(new Set());
  const [currentPhase, setCurrentPhase] = useState(1);
  const [selectedGoals, setSelectedGoals] = useState<Set<GoalType>>(new Set());
  const [selectedRetirementTypes, setSelectedRetirementTypes] = useState<Set<RetirementType>>(
    new Set(),
  );
  const [timeHorizons, setTimeHorizons] = useState<TimeHorizonCollection>({});
  const [riskAnswers, setRiskAnswers] = useState<QuestionAssessmentResults>({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [currentRiskQuestion, setCurrentRiskQuestion] = useState(0);

  // Risk assessment is always required for account creation.
  const totalPhases = 3;

  // Initialize and sync state variables with computed variables
  useEffect(() => {
    // setExistingoalList(new Set(existingGoals));
    setSelectedGoals(new Set(existingGoals));
  }, [existingGoals]);

  useEffect(() => {
    // setExistingRetirementTypeList(new Set(existingRetirementTypes));
    setSelectedRetirementTypes(new Set(existingRetirementTypes));
  }, [existingRetirementTypes]);

  useEffect(() => {
    if (!retakeRiskOnly) return;
    setCurrentPhase(3);
    setCurrentRiskQuestion(0);
  }, [retakeRiskOnly]);

  const yearOptions = Array.from({ length: 30 }, (_, i) => i + 1);

  // all retirement types
  const allRetirementTypes: RetirementOption[] = [
    {
      type: 'TRADITIONAL',
      title: 'Traditional IRA',
      description:
        'A tax-deferred retirement account where contributions may be tax-deductible, and withdrawals in retirement are taxed as income.',
    },
    {
      type: 'ROLLOVER',
      title: 'Rollover IRA',
      description:
        "A tax-deferred retirement account that lets you move funds from a previous employer's 401(k) or retirement plan without incurring taxes or penalties.",
    },
    {
      type: 'ROTH',
      title: 'Roth IRA',
      description:
        'A retirement account where contributions are made with after-tax dollars, allowing tax-free growth and tax-free withdrawals in retirement.',
    },
    {
      type: 'TAXABLE',
      title: 'Taxable',
      description:
        'An investment account in which contributions are made with after-tax dollars, and any investment gains are subject to capital gains tax when assets are sold, but with no restrictions on withdrawals.',
    },
  ];

  const availableGoals: GoalOption[] = [
    {
      goal: 'Just Invest',
      icon: <DollarOutlined />,
    },
    {
      goal: 'Retirement',
      icon: <BankOutlined />,
    },
    {
      goal: 'Major Purchase',
      icon: <HomeOutlined />,
    },
    {
      goal: 'Supplement Income',
      icon: <PlusOutlined />,
    },
    {
      goal: 'High Yield Cash',
      icon: <BankOutlined />,
    },
  ];

  // check if ready to save
  const readyToSave = useMemo(() => {
    if (currentPhase === 1) {
      // Phase 1: check if goals are selected
      if (isFromAddAccount) {
        // check if any new goals are selected (excluding existing ones)
        const hasNewNonRetirementGoals = Array.from(selectedGoals).some(
          (goal) => goal !== 'Retirement' && !existingGoals.has(goal),
        );

        // check if any new retirement subtypes are selected
        const hasNewRetirementTypes = Array.from(selectedRetirementTypes).some(
          (type) => !existingRetirementTypes.has(type),
        );

        // if Retirement is selected, it must have new retirement subtypes
        // OR there must be new non-retirement goals
        if (selectedGoals.has('Retirement')) {
          // If only retirement is selected, must have new retirement subtypes
          // If retirement + other goals are selected, either new retirement subtypes OR new non-retirement goals
          return hasNewRetirementTypes || hasNewNonRetirementGoals;
        }

        // If no retirement is selected, just check for new non-retirement goals
        return hasNewNonRetirementGoals;
      } else {
        const hasExistingGoal = availableGoals.some(({ disabled }) => disabled);
        const hasValidSelection = selectedGoals.size > 0;
        return hasExistingGoal || hasValidSelection;
      }
    } else if (currentPhase === 2) {
      // Phase 2: check if time horizons are set for all selected goals
      const selectedGoalsList = Array.from(selectedGoals).filter(
        (goal) => !existingGoals.has(goal),
      );

      // check if time horizons are set for all selected goals
      const regularGoalsComplete = selectedGoalsList
        .filter((goal) => goal !== 'Retirement')
        .every((goal) => {
          return timeHorizons[goal] && timeHorizons[goal] > 0;
        });

      return regularGoalsComplete;
    } else if (currentPhase === 3) {
      // Phase 3: check if the current risk question is answered
      return riskAnswers[currentRiskQuestion] !== undefined;
    }

    return false;
  }, [
    currentPhase,
    selectedGoals,
    selectedRetirementTypes,
    timeHorizons,
    riskAnswers,
    currentRiskQuestion,
    existingGoals,
    existingRetirementTypes,
    isFromAddAccount,
  ]);

  const handleGoalSelect = (goal: GoalType, selected: boolean) => {
    const newSelectedGoals = new Set(selectedGoals);

    if (selected) {
      newSelectedGoals.add(goal);
    } else {
      newSelectedGoals.delete(goal);
      // if Retirement is selected, clear all retirement subtypes
      if (goal === 'Retirement') {
        setSelectedRetirementTypes(new Set());
      }
    }

    setSelectedGoals(newSelectedGoals);
  };

  const handleRetirementTypeSelect = (type: RetirementType, selected: boolean) => {
    if (existingRetirementTypes.has(type)) return; // existing types cannot be selected
    const newSelectedTypes = new Set(selectedRetirementTypes);

    if (selected) {
      newSelectedTypes.add(type);
    } else {
      newSelectedTypes.delete(type);
    }

    setSelectedRetirementTypes(newSelectedTypes);
  };

  const handleTimeHorizonChange = (key: string, value: number) => {
    setTimeHorizons((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleRiskAnswerSelect = (questionIndex: number, answerId: string) => {
    setRiskAnswers((prev) => ({
      ...prev,
      [questionIndex]: answerId,
    }));
  };

  const handleBack = () => {
    if (currentPhase === 1) {
      window.history.back();
    } else if (currentPhase === 3 && currentRiskQuestion > 0) {
      // in risk assessment phase, if not the first question, go back to the previous question
      setCurrentRiskQuestion(currentRiskQuestion - 1);
    } else if (currentPhase === 3 && retakeRiskOnly) {
      navigate(returnPath);
    } else {
      setCurrentPhase(currentPhase - 1);
    }
  };

  const handleNext = async () => {
    if (currentPhase === 1) {
      setCurrentPhase(2);
    } else if (currentPhase === 2) {
      setCurrentPhase(3);
      setCurrentRiskQuestion(0); // reset risk question index to 0
    } else if (currentPhase === 3) {
      // check if there are more risk questions
      if (currentRiskQuestion < RISK_QUESTIONS.length - 1) {
        // check if the current question is answered
        if (riskAnswers[currentRiskQuestion] !== undefined) {
          setCurrentRiskQuestion(currentRiskQuestion + 1);
        } else {
          message.error('Please answer the current question before proceeding');
        }
      } else {
        // all risk questions are answered, show confirm modal
        setIsConfirmModalVisible(true);
      }
    }
  };

  const handleConfirmProceed = async () => {
    setIsConfirmModalVisible(false);

    const selectedGoalsList = Array.from(selectedGoals).filter((goal) => !existingGoals.has(goal));
    // build account types to create
    const accountTypesToCreate: AccountType[] = [];

    selectedGoalsList.forEach((goal) => {
      if (!existingGoals.has(goal)) {
        if (goal === 'Retirement') {
          selectedRetirementTypes.forEach((retirementType) => {
            if (!existingRetirementTypes.has(retirementType)) {
              accountTypesToCreate.push(`RETIREMENT_${retirementType}`);
            }
          });
        } else {
          // map other goal types
          const typeMapping: Record<string, string> = {
            'Just Invest': 'JUST_INVEST',
            'Major Purchase': 'MAJOR_PURCHASE',
            'Supplement Income': 'SUPPLEMENTAL_INCOME',
            'High Yield Cash': 'HIGH_YIELD_CASH',
          };
          accountTypesToCreate.push(typeMapping[goal] as AccountType);
        }
      }
    });

    // Always calculate recommendation from this run's risk assessment answers.
    const dist = calculateFinalDistribution(riskAnswers, RISK_QUESTIONS as Question[]);

    const { preservation, yield: yieldPercentage, growth } = dist;
    const combinedPreservationYield = preservation + yieldPercentage;

    const { growth: growthPercentage, capitalPreservation: preservationPercentage } =
      adjustAllocations(growth, combinedPreservationYield);

    const recommendation: Recommendation = {
      'Capital Preservation': preservationPercentage,
      Growth: growthPercentage,
    };

    if (retakeRiskOnly) {
      const roundedRiskScore = Math.max(1, Math.round(growthPercentage));
      navigate(returnPath, {
        state: {
          retakeRiskScore: roundedRiskScore,
          accountId: retakeAccountId,
        },
      });
      return;
    }

    navigate('/wealth/open-brokerage-account/step2', {
      state: {
        selectedGoals: selectedGoalsList,
        selectedRetirementTypes: Array.from(selectedRetirementTypes).filter(
          (type) => !existingRetirementTypes.has(type),
        ),
        accountTypesToCreate,
        timeHorizons,
        recommendation,
        hasExistingAccounts: existingGoals.size > 0,
      },
    });
  };

  const handleConfirmCancel = () => {
    setIsConfirmModalVisible(false);
  };

  // const showModal = () => {
  //   setIsModalVisible(true);
  // };

  const handleModalOk = () => {
    setIsModalVisible(false);
  };

  const getTitle = () => {
    if (currentPhase === 1) {
      return 'What are your goals for your money?';
    } else if (currentPhase === 2) {
      return 'Time Horizon';
    } else if (currentPhase === 3) {
      return 'Risk Assessment';
    }
    return '';
  };

  const getSubtitle = () => {
    if (currentPhase === 1) {
      return 'Select all that apply';
    } else if (currentPhase === 2) {
      if (existingGoals.size > 0) {
        return 'Define Your Time Horizon (will attempt to reuse allocations from existing accounts)';
      }
      return 'Define Your Time Horizon';
    } else if (currentPhase === 3) {
      return 'Help us understand your risk tolerance and investment preferences to create a portfolio that aligns with your comfort level and financial goals.';
    }
    return '';
  };

  const renderGoalOptions = () => {
    return availableGoals.map((option) => {
      const isSelected = selectedGoals.has(option.goal);
      const alreadyCreated = existingGoals.has(option.goal);
      return (
        <div key={option.goal}>
          <div
            className={`goal-option ${isSelected ? 'selected' : ''} ${alreadyCreated ? 'disabled' : ''}`}
            onClick={() => {
              if (!alreadyCreated) {
                handleGoalSelect(option.goal, !selectedGoals.has(option.goal));
              }
            }}
          >
            <div className="goal-left">
              {/* <span className="goal-icon">{option.icon}</span> */}
              <span className="goal-label">{option.goal}</span>
            </div>
            <div className="goal-right">
              <div className={`custom-checkbox ${isSelected ? 'checked' : ''}`}>
                {isSelected && <div className="checkbox-inner" />}
              </div>
            </div>
          </div>

          {option.goal === 'Retirement' &&
            (selectedGoals.has('Retirement') || existingRetirementTypes.size > 0) && (
              <div className="sub-options">
                {allRetirementTypes.map((retirementOption) => {
                  const alreadyCreated = existingRetirementTypes.has(retirementOption.type);
                  const isRetirementSelected = selectedRetirementTypes.has(retirementOption.type);
                  return (
                    <div key={retirementOption.type} className="sub-option-wrapper">
                      <div
                        className={`sub-option ${isRetirementSelected ? 'selected' : ''} ${alreadyCreated ? 'disabled' : ''}`}
                        onClick={() => {
                          if (alreadyCreated) return;
                          handleRetirementTypeSelect(
                            retirementOption.type,
                            !selectedRetirementTypes.has(retirementOption.type),
                          );
                        }}
                      >
                        <div className="sub-left">
                          <span className="sub-label">{retirementOption.title}</span>
                        </div>
                        <div className="sub-right">
                          <div
                            className={`custom-checkbox ${isRetirementSelected ? 'checked' : ''}`}
                          >
                            {isRetirementSelected && <div className="checkbox-inner" />}
                          </div>
                        </div>
                      </div>
                      <p className={`sub-description ${alreadyCreated ? 'disabled-text' : ''}`}>
                        {retirementOption.description}
                        {alreadyCreated && ' (Account created)'}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      );
    });
  };

  const renderTimeHorizonForm = () => {
    const selectedGoalsList = Array.from(selectedGoals).filter((goal) => !existingGoals.has(goal));

    return (
      <Form form={form} name="timeHorizon" layout="vertical" size="large" className="phase-form">
        {/* time horizon for regular goals */}
        {selectedGoalsList.map((goal) => (
          <div key={goal} className="form-row">
            <Form.Item
              label={`${goal}: ${GOAL_QUESTIONS[goal]}`}
              className="form-item single"
              rules={[
                {
                  required: true,
                  message: `Please select time horizon for ${goal}`,
                },
              ]}
            >
              <Select
                placeholder="Select years"
                value={timeHorizons[goal]}
                onChange={(value) => handleTimeHorizonChange(goal, value)}
                suffixIcon={<span style={{ color: '#666', fontSize: '14px' }}>Year</span>}
              >
                {yearOptions.map((year) => (
                  <Option key={year} value={year}>
                    {year === 30 ? '30 or more' : year}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        ))}
      </Form>
    );
  };

  const renderRiskAssessmentForm = () => {
    const currentQuestion = RISK_QUESTIONS[currentRiskQuestion];

    return (
      <div className="risk-assessment-container">
        <div className="risk-progress-container">
          <div className="risk-progress-bar">
            <div
              className="risk-progress-fill"
              style={{
                width: `${((currentRiskQuestion + 1) / RISK_QUESTIONS.length) * 100}%`,
              }}
            />
          </div>
          <div className="risk-progress-text">
            Question {currentRiskQuestion + 1} of {RISK_QUESTIONS.length}
          </div>
        </div>

        <div className="risk-question-wrapper">
          <div className="risk-question">
            <h3 className="question-title">{currentQuestion.question}</h3>
          </div>
          <div className="sub-options">
            {currentQuestion.answers.map((answer) => {
              const isSelected = riskAnswers[currentRiskQuestion] === answer.id;

              return (
                <div key={answer.id} className="sub-option-wrapper">
                  <div
                    className={`sub-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleRiskAnswerSelect(currentRiskQuestion, answer.id)}
                  >
                    <div className="sub-left">
                      <span className="sub-label">{answer.answer}</span>
                    </div>
                    <div className="sub-right">
                      <div className={`custom-checkbox ${isSelected ? 'checked' : ''}`}>
                        {isSelected && <div className="checkbox-inner" />}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentPhase = () => {
    if (currentPhase === 1) {
      return <div className="goals-container">{renderGoalOptions()}</div>;
    } else if (currentPhase === 2) {
      return <div className="form-container">{renderTimeHorizonForm()}</div>;
    } else {
      return <div className="risk-container">{renderRiskAssessmentForm()}</div>;
    }
  };

  const getNextButtonText = () => {
    if (currentPhase === 3) {
      if (currentRiskQuestion < RISK_QUESTIONS.length - 1) {
        return 'Next Question';
      } else {
        if (retakeRiskOnly) return 'Update score';
        return isFromAddAccount ? 'Add account' : 'Next';
      }
    } else if (currentPhase === totalPhases) {
      return isFromAddAccount ? 'Add account' : 'Next';
    }
    return 'Next';
  };

  return (
    <div className="step1-container">
      <div className="header">
        <span className="back-button" onClick={handleBack}>
          <LeftOutlined />
        </span>
        <h1 className="title">{getTitle()}</h1>
      </div>

      <div className="content">
        {/* main progress bar */}
        <div className="progress-container">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(currentPhase / totalPhases) * 100}%` }}
            />
          </div>
          <div className="progress-text">
            {currentPhase}/{totalPhases}
          </div>
        </div>

        <div className="subtitle-container">
          <h2 className="subtitle">{getSubtitle()}</h2>
          {/* <QuestionCircleOutlined 
            className="help-icon"
            onClick={showModal}
          /> */}
        </div>

        {apexGoalsReady ? (
          renderCurrentPhase()
        ) : (
          <div className="goals-loading" data-testid="apex-goals-loading">
            <Spin size="large" />
          </div>
        )}

        <div className="footer">
          <Button
            type="primary"
            size="large"
            onClick={handleNext}
            disabled={!apexGoalsReady || !readyToSave}
            className="next-button"
          >
            {getNextButtonText()}
          </Button>
        </div>
      </div>

      <Modal
        title="What are the investment goals?"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalOk}
        footer={[
          <Button key="got-it" type="primary" onClick={handleModalOk}>
            Got it
          </Button>,
        ]}
        className="goals-modal"
      >
        <div className="modal-content">
          <h3>Just Invest</h3>
          <p>
            A general investment account for building wealth over time without specific restrictions
            or tax advantages.
          </p>

          <h3>Retirement</h3>
          <p>
            Tax-advantaged accounts designed to help you save for retirement, including Traditional
            IRA, Roth IRA, Rollover IRA, and Taxable retirement accounts.
          </p>

          <h3>Major Purchase</h3>
          <p>
            Investment account for saving towards a significant future purchase like a home, car, or
            other major expense.
          </p>

          <h3>Supplement Income</h3>
          <p>
            Investment account focused on generating additional income to supplement your regular
            earnings.
          </p>

          <h3>High Yield Cash</h3>
          <p>
            A cash management account offering higher yields while maintaining liquidity and capital
            preservation.
          </p>
        </div>
      </Modal>

      {/* confirm modal */}
      <Modal
        title="Confirmation"
        open={isConfirmModalVisible}
        onOk={handleConfirmProceed}
        onCancel={handleConfirmCancel}
        footer={[
          <Button key="cancel" onClick={handleConfirmCancel}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmProceed}>
            {retakeRiskOnly ? 'Submit' : 'Confirm'}
          </Button>,
        ]}
        className="confirm-modal"
      >
        <div className="modal-content">
          <p>
            {retakeRiskOnly
              ? 'Submit your updated risk questionnaire answers?'
              : isFromAddAccount
                ? "A new managed account will be created based on your selected goal. Please confirm that you'd like to proceed with opening this account. Yields are variable and may change over time."
                : "You can set financial goals without a brokerage account, but you'll need one for each goal to take action."}
          </p>
          {!retakeRiskOnly && (
            <p>
              {`Each goal creates a separate brokerage account to keep your investments organized. Would you like to proceed?`}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Step1;
